/**
 * VAGUE 34 — Login reminder for telepros.
 *
 * Modal shown once per day (localStorage) with the 4 golden rules :
 *   1) Toujours donner ton code télépro si le prospect est intéressé
 *   2) Toujours demander si le prospect a besoin d'un site web
 *   3) Sur PC/tablette : mode Liste
 *   4) Sur smartphone : mode Simple
 *
 * Non-dismissible until "J'ai compris" is clicked → the code path in the
 * portal now literally can't miss the reminder.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  IdentificationBadge, Globe, ListDashes, Phone, CheckCircle, Sparkle, DeviceMobile,
} from "@phosphor-icons/react";

const KEY_PREFIX = "mc68_telepro_daily_reminder_";

export default function TeleproDailyReminder({ teleproCode }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!teleproCode) return;
    // Show once per calendar day per telepro code
    const today = new Date().toISOString().slice(0, 10);
    const key = `${KEY_PREFIX}${teleproCode}_${today}`;
    if (localStorage.getItem(key) === "1") return;
    // Wait 400ms so it doesn't jump-scare the user
    const t = setTimeout(() => setOpen(true), 400);
    return () => clearTimeout(t);
  }, [teleproCode]);

  const acknowledge = () => {
    const today = new Date().toISOString().slice(0, 10);
    localStorage.setItem(`${KEY_PREFIX}${teleproCode}_${today}`, "1");
    setOpen(false);
    toast.success("Prêt·e à tout déchirer ! 💪");
  };

  if (!open) return null;

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm grid place-items-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="telepro-daily-reminder"
    >
      <div className="bg-card border border-primary/30 rounded-sm max-w-lg w-full shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-primary text-primary-foreground p-5 text-center">
          <div className="text-3xl mb-1">☀️</div>
          <h2 className="font-display text-lg font-medium">Bonne journée, {teleproCode?.split("-")[0] || "champion·ne"} !</h2>
          <p className="text-xs opacity-90 mt-0.5">Avant de démarrer, 5 rappels essentiels 👇</p>
        </div>

        {/* Body */}
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          {/* Rule 1 — Code partenaire */}
          <div className="flex items-start gap-3 p-3 bg-primary/5 rounded-sm" data-testid="reminder-rule-code">
            <div className="shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground grid place-items-center font-bold">1</div>
            <div className="flex-1">
              <div className="font-bold text-sm flex items-center gap-1"><IdentificationBadge size={16} weight="duotone" className="text-primary"/> Donne toujours ton code partenaire</div>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Si le prospect est intéressé (RDV, réflexion, envoi info…),
                <b className="text-foreground"> donne-lui ton code télépro</b> (ex : <span className="font-mono bg-accent px-1 rounded">DONA-001</span>) pour qu'il te soit crédité si il souscrit ensuite.
              </p>
            </div>
          </div>

          {/* Rule 2 — Site web */}
          <div className="flex items-start gap-3 p-3 bg-primary/5 rounded-sm" data-testid="reminder-rule-website">
            <div className="shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground grid place-items-center font-bold">2</div>
            <div className="flex-1">
              <div className="font-bold text-sm flex items-center gap-1"><Globe size={16} weight="duotone" className="text-primary"/> Demande toujours s'il a besoin d'un site web</div>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Beaucoup de commerçants n'en ont pas ou l'ont abandonné.
                <b className="text-foreground"> Note "besoin site web : oui"</b> dans la fiche prospect — c'est une vente additionnelle facile pour toi.
              </p>
            </div>
          </div>

          {/* Rule 3 — Mode Liste */}
          <div className="flex items-start gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-sm" data-testid="reminder-rule-list-mode">
            <div className="shrink-0 w-8 h-8 rounded-full bg-emerald-500 text-white grid place-items-center font-bold">3</div>
            <div className="flex-1">
              <div className="font-bold text-sm flex items-center gap-1"><ListDashes size={16} weight="duotone" className="text-emerald-700"/> Sur PC / tablette → mode <u>Liste</u></div>
              <p className="text-xs text-emerald-900 mt-1 leading-relaxed">
                Ouvre "<b>Ma liste du jour</b>" → clique sur chaque ligne → renseigne le statut (RDV / refus / no_answer / vente).
                <b className="text-emerald-900"> Chaque clic = 1 appel enregistré</b> qui apparaît dans ton bilan.
              </p>
            </div>
          </div>

          {/* Rule 4 — Mode Simple sur mobile */}
          <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-sm" data-testid="reminder-rule-simple-mode">
            <div className="shrink-0 w-8 h-8 rounded-full bg-blue-500 text-white grid place-items-center font-bold">4</div>
            <div className="flex-1">
              <div className="font-bold text-sm flex items-center gap-1"><DeviceMobile size={16} weight="duotone" className="text-blue-700"/> Sur smartphone → mode <u>Simple</u></div>
              <p className="text-xs text-blue-900 mt-1 leading-relaxed">
                Le mode Liste est trop dense sur mobile.
                <b className="text-blue-900"> Bascule sur "Mode Simple"</b> (bouton en haut) : une fiche prospect à la fois, gros boutons pour renseigner rapidement.
              </p>
            </div>
          </div>

          {/* Rule 5 — Accompagner le prospect jusqu'à l'inscription (VAGUE 35) */}
          <div className="flex items-start gap-3 p-3 bg-amber-50 border-2 border-amber-300 rounded-sm" data-testid="reminder-rule-signup-coach">
            <div className="shrink-0 w-8 h-8 rounded-full bg-amber-500 text-white grid place-items-center font-bold">5</div>
            <div className="flex-1">
              <div className="font-bold text-sm flex items-center gap-1">🎯 Guide le prospect jusqu'à l'inscription (NOUVEAU)</div>
              <p className="text-xs text-amber-900 mt-1 leading-relaxed">
                <b>Problème récurrent</b> : 20+ prospects ont déjà abandonné faute d'être guidés. Pendant l'appel, dis mot pour mot :
              </p>
              <div className="text-xs text-amber-900 mt-2 bg-white/70 border border-amber-200 rounded-sm p-2 italic leading-relaxed">
                "Je vous envoie <b>par SMS/WhatsApp</b> un lien, cliquez dessus, mon code est déjà pré-rempli.
                Il vous suffit de : 1) saisir votre <b>email</b>, 2) choisir un <b>mot de passe</b>,
                3) cliquer <b>"Créer mon compte"</b>, 4) sur votre tableau de bord, cliquer <b>"Revendiquer ma fiche"</b> → c'est fait, vous avez vos 14 jours d'essai gratuit."
              </div>
              <p className="text-xs text-amber-900 mt-2 leading-relaxed">
                <b>Utilise le bouton "Copier mon lien d'inscription"</b> en haut de ton portail — il contient déjà ton code.
                Reste en ligne quelques minutes au cas où le prospect bloque sur une étape.
              </p>
            </div>
          </div>

          {/* Reminder about the daily recap */}
          <div className="text-[11px] text-muted-foreground italic border-t border-border pt-3 flex items-start gap-2">
            <Sparkle size={12} className="shrink-0 mt-0.5"/>
            <span>
              <b>Rappel important</b> : si tu ne cliques pas sur les statuts,
              ton <b>bilan de fin de journée</b> affichera 0 appel — même
              si tu as passé la journée au téléphone. Le portail ne peut
              compter que ce que tu enregistres.
            </span>
          </div>
        </div>

        {/* CTA */}
        <div className="p-4 bg-accent/30 border-t border-border">
          <button
            onClick={acknowledge}
            className="w-full bg-primary text-primary-foreground hover:bg-[#0f3d29] px-4 py-3 rounded-sm font-semibold text-sm inline-flex items-center justify-center gap-2"
            data-testid="reminder-acknowledge"
          >
            <CheckCircle size={16} weight="fill"/> J'ai compris — c'est parti !
          </button>
          <p className="text-[10px] text-center text-muted-foreground mt-2">
            Ce rappel apparaît 1 seule fois par jour.
          </p>
        </div>
      </div>
    </div>
  );
}
